import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types'
import { Placeholder } from '@leshen/ui'
import { Link as GatsbyLink } from 'gatsby'
import { getImage } from 'gatsby-plugin-image'
import { renderRichText } from 'gatsby-source-contentful/rich-text'
import React from 'react'

import {
  Accordion,
  AuthorLine,
  AvailabilityAddressForm,
  AvailabilityForm,
  Banner,
  Billboard,
  Brandy,
  Carousel,
  carouselConfig,
  Cart,
  ChannelLineupWithData,
  Column,
  Columns,
  CountdownTimer,
  CtaBar,
  // FeaturedPosts,
  FormWithMapi,
  Hero,
  Image,
  ItemBank,
  LeshenPhoneCTA,
  Link,
  LinkButton,
  List,
  ListItem,
  MegaForm,
  ModalWithState,
  PackageCard,
  Price,
  Pullquote,
  SpeedTest,
  SplitContent,
  SplitModule,
  Stack,
  StateDropdown,
  StepsData,
  SVGImage,
  Table,
  Tabs,
  Typography,
  VariableContent,
  Video,
} from '@leshen/gatsby-theme-leshen'

import determineImage from './determineImage'

import removeEmptyParagraphs from './removeEmptyParagraphs'

import { ANCHOR } from '../../ui/src/utils/dataTrackingValues'

import RichTextDisclaimer from '../src/components/RichTextDisclaimer/RichTextDisclaimer'

/**
 * Returns a Placeholder component with a warning or error message when a component cannot be found during
 * the mapRichText methods run.
 * @param {string | undefined} contentType - displays an error if undefined, or a message with the
 * name of the missing component (contentType) if a string is passed.
 * @returns {JSX} React Placeholder component
 */
const getPlaceholder = (contentType) => {
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line
    console.error(
      'mapRichText encountered an error when trying to map an embedded entry. This method is part of `gatsby-theme-content`.'
    )
  }

  const missingDataErrorMessage = `No data could be found for this component. If you think the data should exist, A clean rebuild may be required.`
  const defaultMessage = `The "${contentType}" component can't be rendered here. It is
          most likely missing support from the core.`
  return (
    <Placeholder type={contentType ? 'warning' : 'error'}>
      <Typography variant="small">
        {contentType ? 'Missing Component' : 'Error'}
      </Typography>
      <Typography>
        {contentType ? defaultMessage : missingDataErrorMessage}
      </Typography>
    </Placeholder>
  )
}

const formatBackgroundColor = (backgroundColor) =>
  backgroundColor?.toLowerCase().replace(' ', '')

// This is comming from the query in Default.jsx
let packages

const getGradientVariant = (variant) =>
  variant?.replace('Gradient - ', '').replace(' ', '')

/**
 * If no node, or subsequent required fields are given, the error placeholder
 * will render from the "default" case within the switch.
 */
export const mapRichTextEmbed = (node) => {
  /**
   * If node.data is an empty object, the GraphQL query may be
   * missing the `contentful_id`, which is used to bind the
   * reference to it's `raw` output.
   */
  const fields = node?.data?.target
  if (!fields) {
    return (
      <Placeholder type="error">
        <Typography variant="small">Error:</Typography>
        <Typography>
          No data could be found for this entry. This should not occur and may
          indicate an improperly setup GraphQL query for the entry type
        </Typography>
      </Placeholder>
    )
  }

  /**
   * We can't pull the rest of the fields off yet because
   * they are unique to each content model.
   */

  const { __typename: contentType } = fields

  switch (contentType) {
    case 'ContentfulSpeedTest': {
      return <SpeedTest id={fields?.speedTestId} style={fields?.style} />
    }
    case 'ContentfulCountdownTimer': {
      return (
        <CountdownTimer
          key={fields.id}
          startDate={fields?.startDate}
          endDate={fields.endDate}
          text={
            fields?.countdownText &&
            renderRichText(fields?.countdownText, mapRichText()) // eslint-disable-line
          }
          style={fields?.style}
        />
      )
    }
    case 'ContentfulDisclaimer': {
      const { identifier, brandy } = fields

      if (fields?.disclaimerReference?.sys?.urn) {
        const content = fields?.brandDisclaimer?.content
        return (
          <RichTextDisclaimer
            content={renderRichText(content, mapRichText())} // eslint-disable-line
          />
        )
      }

      return <RichTextDisclaimer identifier={identifier} brandy={brandy} />
    }
    case 'ContentfulBrandDisclaimer': {
      const { content } = fields
      return (
        <Typography variant="legal">
          {/* eslint-disable-next-line no-use-before-define */}
          {renderRichText(content, mapRichText())}
        </Typography> // eslint-disable-line
      )
    }
    case 'ContentfulEyebrow': {
      const { eyebrow, eyebrowTag } = fields
      return (
        <Typography variant="h6" rendersAs={eyebrowTag}>
          {eyebrow}
        </Typography>
      )
    }
    case 'ContentfulCustomText': {
      const { headingContent, tag, styleAs } = fields
      const style = styleAs?.toLowerCase()

      // eslint-disable-next-line no-use-before-define
      const headingText = renderRichText(headingContent, mapRichText()).map(
        (text) =>
          text.props.children.map((headingItem) => {
            if (headingItem.type === 'sup') {
              return <sup>{headingItem.props.children[1]}</sup>
            }

            if (headingItem.type === 'sub') {
              return <sub>{headingItem.props.children[1]}</sub>
            }

            return headingItem
          })
      )

      return (
        <Typography variant={style} rendersAs={tag}>
          {headingText.map((text) => (
            <>{text}</>
          ))}
        </Typography>
      )
    }
    case 'ContentfulBasicTable': {
      return (
        <Table
          key={fields.tableName}
          leftColumnAlignment={fields.leftColumnAlignment}
          mobileComparison={fields.mobileComparison}
        >
          {/* we can disable the next line because the order of the functions being defined doesn't matter since they are all called properly in the end. everything is called in the last function: mapSections, which at that point has everything defined  */}
          {/* eslint-disable-next-line */}
          {renderRichText(fields.mainContent, mapRichText())}
        </Table>
      )
    }
    case 'ContentfulButton': {
      const { outlined, text, mobileText, file, link, visibility, cobra } =
        fields
      let { color } = fields

      const leftMedia =
        module?.leftIcon?.cloudinary?.[0]?.gatsbyImageData ?? null
      const leftSvg = module?.leftIcon?.cloudinary?.[0]?.svg ?? null
      const rightMedia =
        module?.rightIcon?.cloudinary?.[0]?.gatsbyImageData ?? null
      const rightSvg = module?.rightIcon?.cloudinary?.[0]?.svg ?? null

      const LeftIcon = determineImage({
        svg: leftSvg,
        cloudinary: leftMedia,
        contentful: module.leftIcon,
      })

      const RightIcon = determineImage({
        svg: rightSvg,
        cloudinary: rightMedia,
        contentful: module.rightIcon,
      })

      /**
       * Yes, really. The data comes back this way from Contentful.
       * It's possible it's a silly model setup in Contentful.
       */
      const linkUrl = link || file?.file?.file?.url

      const gradient = color?.includes('Gradient')
      color = getGradientVariant(color)
      const visibilityValue =
        typeof visibility === 'string' ? visibility.toLowerCase() : ''
      const cobraValue = typeof cobra === 'string' ? cobra.toLowerCase() : ''

      return (
        <LinkButton
          to={linkUrl}
          CustomLink={GatsbyLink}
          color={color ? color.toLowerCase() : 'primary'}
          outlined={outlined}
          gradient={gradient}
          leftIcon={LeftIcon}
          rightIcon={RightIcon}
          mobileText={mobileText}
          visibility={visibilityValue}
          cobra={cobraValue}
        >
          {text}
        </LinkButton>
      )
    }
    case 'ContentfulPhoneNumber': {
      const {
        hidePhoneNumberOnMobile,
        staticPhoneNumber,
        outlined,
        cobra,
        cobraButtonText,
        cobraCartUrl,
        mobileText,
        visibility,
      } = fields
      let { color } = fields
      const gradient = color?.includes('Gradient')
      color = getGradientVariant(color)
      const visibilityValue =
        typeof visibility === 'string' ? visibility.toLowerCase() : ''

      return (
        <LeshenPhoneCTA
          hidePhoneNumberOnMobile={hidePhoneNumberOnMobile}
          staticPhoneNumber={staticPhoneNumber}
          color={color ? color.toLowerCase() : 'primary'}
          outlined={outlined}
          gradient={gradient}
          cobra={cobra}
          cobraButtonText={cobraButtonText}
          cobraCartUrl={cobraCartUrl}
          mobileText={mobileText}
          visibility={visibilityValue}
        >
          {fields.text}
        </LeshenPhoneCTA>
      )
    }
    case 'ContentfulAvailabilityAddressForm': {
      const {
        identifier,
        labelText,
        buttonText,
        buttonOutlined,
        placeholderText,
      } = fields

      let { buttonColor } = fields
      const gradient = buttonColor?.includes('Gradient')
      buttonColor = getGradientVariant(buttonColor)

      return (
        <AvailabilityAddressForm
          key={identifier}
          label={labelText}
          placeholderText={placeholderText}
          buttonText={buttonText}
          buttonColor={buttonColor.toLowerCase()}
          buttonOutlined={buttonOutlined}
          buttonGradient={gradient}
        />
      )
    }
    case 'ContentfulAvailabilityZipForm': {
      const {
        identifier,
        labelText,
        buttonText,
        buttonOutlined,
        placeholderText,
        formVisibility,
      } = fields

      let { buttonColor } = fields
      const gradient = buttonColor?.includes('Gradient')
      buttonColor = getGradientVariant(buttonColor)

      return (
        <AvailabilityForm
          key={identifier}
          label={labelText}
          buttonText={buttonText}
          placeholderText={placeholderText}
          buttonColor={buttonColor.toLowerCase()}
          buttonOutlined={buttonOutlined}
          buttonGradient={gradient}
          formVisibility={formVisibility}
        />
      )
    }
    case 'ContentfulPrice': {
      const { brandy, variant, modal, showFeaturedBullets } = fields

      /* Disabling linter because mapRichText is being called before it's defined.
       However that's not actually the case when you follow through the logic of the code */
      /* eslint-disable */

      return (
        <Price
          standalone={true}
          className="price embedded-entry"
          {...brandy?.price}
          bullets={brandy.bullets}
          variant={variant}
          showFeaturedBullets={showFeaturedBullets}
          nutrition={brandy?.nutrition}
        >
          {modal && (
            <ModalWithState
              modalText={renderRichText(modal.modalTrigger, mapRichText())}
              heading={modal?.heading}
              noExit={modal?.noExit}
            >
              {renderRichText(modal?.mainContent, mapRichText())}
            </ModalWithState>
          )}
        </Price>
      )
      /* eslint-enable */
    }
    case 'ContentfulVideo': {
      const {
        childrenContentfulVideoCloudinaryJsonNode,
        childContentfulVideoIframeTextNode,
        width,
        height,
      } = fields
      const videoUrl =
        childrenContentfulVideoCloudinaryJsonNode?.[0]?.secure_url
      const iframe = childContentfulVideoIframeTextNode?.iframe
      return (
        <Video
          width={width}
          height={height}
          iframe={iframe}
          url={videoUrl}
          poster={fields?.thumbnail?.cloudinary[0]?.secure_url}
        />
      )
    }
    case 'ContentfulStateDropdown':
      return (
        <StateDropdown
          key={fields.id}
          aoa={fields.aoa}
          spanish={fields.spanish}
        />
      )
    case 'ContentfulMedia': {
      const { borderRadius, customPosition, aspectRatio } = fields || {}

      const media = fields?.cloudinary?.[0]?.gatsbyImageData ?? null
      const svg = fields?.cloudinary?.[0]?.svg ?? null

      return determineImage({
        svg,
        cloudinary: media,
        contentful: fields,
        aspectRatio,
        borderRadius,
        customPosition,
      })
    }
    case 'ContentfulAuthorLine': {
      const { publishDate, author } = fields || {}
      const date = new Date(publishDate)
      const formattedDate = date?.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })

      return (
        <AuthorLine
          publishDate={formattedDate}
          firstName={author?.firstName}
          lastName={author?.lastName}
        />
      )
    }
    default:
      return getPlaceholder(contentType)
  }
}

/**
 * A custom list of render keys to transform rich text from Contentful into components
 * from Clearlink's design system.
 *
 * Used in the 2nd argument of documentToReactComponents.
 */

const headingIsEmpty = (children) =>
  !children || children.length <= 0 || children[0] === ''

export const mapRichText = (options) => ({
  renderNode: {
    [BLOCKS.HEADING_1]: (_, children = []) => {
      if (headingIsEmpty(children)) {
        return null
      }

      return <Typography variant="h1">{children}</Typography>
    },
    [BLOCKS.HEADING_2]: (_, children = []) => {
      if (headingIsEmpty(children)) {
        return null
      }

      return <Typography variant="h2">{children}</Typography>
    },
    [BLOCKS.HEADING_3]: (_, children = []) => {
      if (headingIsEmpty(children)) {
        return null
      }

      return <Typography variant="h3">{children}</Typography>
    },
    [BLOCKS.HEADING_4]: (_, children = []) => {
      if (headingIsEmpty(children)) {
        return null
      }

      return <Typography variant="h4">{children}</Typography>
    },
    [BLOCKS.HEADING_5]: (_, children = []) => {
      if (headingIsEmpty(children)) {
        return null
      }

      return <Typography variant="h5">{children}</Typography>
    },
    [BLOCKS.HEADING_6]: (_, children = []) => {
      if (headingIsEmpty(children)) {
        return null
      }
      return <Typography variant="h6">{children}</Typography>
    },
    [BLOCKS.PARAGRAPH]: (_, children = []) => {
      if (headingIsEmpty(children)) {
        return null
      }

      return <Typography>{children}</Typography>
    },
    [BLOCKS.UL_LIST]: (_, children) => <List>{children}</List>,
    [BLOCKS.OL_LIST]: (_, children) => <List type="ordered">{children}</List>,
    [BLOCKS.LIST_ITEM]: (_, children) => (
      <ListItem listIcon={options?.listIcon}>{children}</ListItem>
    ),
    [BLOCKS.TABLE]: (_, children) => <table>{children}</table>,
    [BLOCKS.EMBEDDED_ENTRY]: mapRichTextEmbed,
    [INLINES.HYPERLINK]: (node = {}, children) => (
      <Link to={node?.data?.uri} data-tracking={ANCHOR.NAME}>
        {children}
      </Link>
    ),
    [INLINES.ENTRY_HYPERLINK]: () => (
      /**
       * For some strange reason this function does not render, even though it is called
       * for each entry hyperlink in rich text.
       *
       * TODO: If there is a demand for this feature, some Contentful schema needs figuring out.
       * It should be a `... on ContentfulAsset` under `mainContent` but that doesn't seem to exist.
       */
      <Placeholder type="warning">
        <Typography variant="small">Content Not Supported Yet.</Typography>
        <Typography>Entry type hyperlinks are not yet supported.</Typography>
      </Placeholder>
    ),
    [INLINES.EMBEDDED_ENTRY]: (node = {}) => {
      const fields = node?.data?.target
      if (!fields) {
        return (
          <Placeholder type="error">
            <Typography variant="small">Error:</Typography>
            <Typography>
              No data could be found for this embedded entry. This should not
              occur and may indicate an improperly setup GraphQL query for the
              entry type
            </Typography>
          </Placeholder>
        )
      }
      /**
       * We can't pull the rest of the fields off yet because
       * they are unique to each content model.
       */
      const { __typename: contentType } = fields
      const megaFormData = fields?.mainContent?.references?.find((item) => {
        if (item?.__typename === 'ContentfulMegaForm') {
          return {
            ...item?.form,
          }
        }

        return false
      })

      switch (contentType) {
        case 'ContentfulSpeedTest': {
          return <SpeedTest id={fields.speedTestId} style={fields?.style} />
        }
        case 'ContentfulDisclaimer': {
          const { identifier, brandy } = fields
          if (fields?.disclaimerReference?.sys?.urn) {
            const content = fields?.brandDisclaimer?.content
            return (
              <RichTextDisclaimer
                content={renderRichText(content, mapRichText())} // eslint-disable-line
              />
            )
          }

          return <RichTextDisclaimer identifier={identifier} brandy={brandy} />
        }
        case 'ContentfulCountdownTimer': {
          return (
            <CountdownTimer
              key={fields.id}
              startDate={fields?.startDate}
              endDate={fields.endDate}
              text={
                fields?.countdownText &&
                renderRichText(fields?.countdownText, mapRichText())
              }
              style={fields?.style}
            />
          )
        }
        case 'ContentfulBrandDisclaimer': {
          const { content } = fields
          return (
            <Typography variant="legal">
              {renderRichText(content, mapRichText())}
            </Typography> // eslint-disable-line
          )
        }
        case 'ContentfulModal': {
          return (
            <ModalWithState
              modalText={renderRichText(fields.modalTrigger, mapRichText())}
              heading={fields.heading}
              noExit={fields.noExit}
              hasMegaForm={!!megaFormData}
            >
              {megaFormData && (
                <MegaForm
                  isInsideModal
                  formId={megaFormData.form.emily.emily_guid}
                  formName={megaFormData.form.emily.name}
                  heading={megaFormData.form.heading}
                  tcpaCheckbox={megaFormData.form.cpaCheckbox}
                  formDisclaimerPosition={
                    megaFormData.form.formDisclaimerPosition
                  }
                  action={megaFormData.form.emily.submit.action}
                  submitButtonText={megaFormData.form.emily.submit.buttonText}
                  fields={megaFormData.form.emily.formFields}
                  inlineButton={megaFormData.form.visibleFieldCount === 1}
                  disclaimer={megaFormData.form.formDisclaimer}
                  stepsData={StepsData}
                  key={megaFormData.form.identifier}
                  spanish={megaFormData.form.spanish}
                  megaFormDisclaimer={megaFormData.form.megaFormDisclaimer}
                />
              )}
              {!megaFormData &&
                renderRichText(fields.mainContent, mapRichText())}
            </ModalWithState>
          )
        }
        case 'ContentfulButton': {
          const {
            identifier,
            outlined,
            text,
            mobileText,
            file,
            link,
            visibility,
            cobra,
          } = fields
          let { color } = fields
          const leftMedia = module?.leftIcon?.cloudinary?.[0]?.gatsbyImageData
          const leftSvg = module?.leftIcon?.cloudinary?.[0]?.svg
          const rightMedia = module?.rightIcon?.cloudinary?.[0]?.gatsbyImageData
          const rightSvg = module?.rightIcon?.cloudinary?.[0]?.svg

          const LeftIcon = determineImage({
            svg: leftSvg,
            cloudinary: leftMedia,
            contentful: module?.leftIcon,
          })

          const RightIcon = determineImage({
            svg: rightSvg,
            cloudinary: rightMedia,
            contentful: module?.rightIcon,
          })

          const gradient = color?.includes('Gradient')

          color = getGradientVariant(color)
          /**
           * Yes, really. The data comes back this way from Contentful.
           * It's possible it's a silly model setup in Contentful.
           */
          const linkUrl = link || file?.file?.file?.url

          if (!linkUrl) {
            return (
              <Placeholder type="warning">
                <Typography variant="small">Missing link</Typography>
                <Typography>{`This ${contentType} is missing a Document or URL link for identifier: ${identifier}`}</Typography>
              </Placeholder>
            )
          }

          const visibilityValue =
            typeof visibility === 'string' ? visibility.toLowerCase() : ''

          const cobraValue =
            typeof cobra === 'string' ? cobra.toLowerCase() : ''

          return (
            <LinkButton
              to={linkUrl}
              CustomLink={GatsbyLink}
              color={color ? color.toLowerCase() : 'primary'}
              outlined={outlined}
              gradient={gradient}
              leftIcon={LeftIcon}
              rightIcon={RightIcon}
              mobileText={mobileText}
              visibility={visibilityValue}
              cobra={cobraValue}
            >
              {text}
            </LinkButton>
          )
        }
        case 'ContentfulPhoneNumber': {
          const {
            hidePhoneNumberOnMobile,
            staticPhoneNumber,
            outlined,
            cobra,
            cobraButtonText,
            cobraCartUrl,
            mobileText,
            visibility,
          } = fields
          let { color } = fields
          const gradient = color?.includes('Gradient')

          color = getGradientVariant(color)
          const visibilityValue =
            typeof visibility === 'string' ? visibility.toLowerCase() : ''

          return (
            <LeshenPhoneCTA
              hidePhoneNumberOnMobile={hidePhoneNumberOnMobile}
              staticPhoneNumber={staticPhoneNumber}
              color={color ? color.toLowerCase() : 'primary'}
              outlined={outlined}
              gradient={gradient}
              cobra={cobra}
              cobraButtonText={cobraButtonText}
              cobraCartUrl={cobraCartUrl}
              mobileText={mobileText}
              visibility={visibilityValue}
            >
              {fields.text}
            </LeshenPhoneCTA>
          )
        }
        case 'ContentfulMedia': {
          const media = fields?.cloudinary?.[0]?.gatsbyImageData ?? null
          const svg = fields?.cloudinary?.[0]?.svg ?? null
          const { aspectRatio, borderRadius, customPosition } = fields

          return determineImage({
            svg,
            cloudinary: media,
            contentful: fields?.media,
            aspectRatio,
            borderRadius,
            customPosition,
          })
        }
        default: {
          return getPlaceholder(contentType)
        }
      }
    },
  },
  renderMark: {
    [MARKS.BOLD]: (text) => <strong>{text}</strong>,
    [MARKS.ITALIC]: (text) => <em>{text}</em>,
    [MARKS.SUPERSCRIPT]: (text) => <sup>{text}</sup>,
    [MARKS.SUBSCRIPT]: (text) => <sub>{text}</sub>,
  },
  // This came from Contentful's docs on how to handle making new lines <br /> tags
  renderText: (text) =>
    typeof text === 'string' &&
    text
      .split('\n')
      .reduce(
        (children, textSegment, index) => [
          ...children,
          index > 0 && <br />,
          textSegment,
        ],
        []
      ),
})

/**
 * A list of render keys, extending mapRichText with property overrides for components
 * that will be rendered inside a Hero component.
 *
 * Used in the 2nd argument of documentToReactComponents.
 */
export const mapRichTextHero = {
  renderNode: {
    ...mapRichText().renderNode,
    [BLOCKS.LIST_ITEM]: (_, children) => (
      <ListItem>
        <Typography variant="h5" rendersAs="span">
          {children}
        </Typography>
      </ListItem>
    ),
    [BLOCKS.EMBEDDED_ENTRY]: (node = {}) => {
      /**
       * If node.data is an empty object, the GraphQL query may be
       * missing the `contentful_id`, which is used to bind the
       * reference to it's `raw` output.
       *
       * If no node, or subsequent required fields are given, the error placeholder
       * will render from the "default" case within the switch.
       */

      const fields = node?.data?.target
      if (!fields) {
        return (
          <Placeholder type="error">
            <Typography variant="small">Error:</Typography>
            <Typography>
              No data could be found for this embedded entry. This should not
              occur and may indicate an improperly setup GraphQL query for the
              entry type
            </Typography>
          </Placeholder>
        )
      }

      /**
       * We can't pull the rest of the fields off yet because
       * they are unique to each content model.
       */
      const { __typename: contentType } = fields

      switch (contentType) {
        case 'ContentfulButton': {
          const {
            identifier,
            outlined,
            text,
            mobileText,
            file,
            link,
            visibility,
            cobra,
          } = fields
          let { color } = fields

          const leftMedia =
            module?.leftIcon?.cloudinary?.[0]?.gatsbyImageData ?? null
          const leftSvg = module?.leftIcon?.cloudinary?.[0]?.svg ?? null
          const rightMedia =
            module?.rightIcon?.cloudinary?.[0]?.gatsbyImageData ?? null
          const rightSvg = module?.rightIcon?.cloudinary?.[0]?.svg ?? null

          const LeftIcon = determineImage({
            svg: leftSvg,
            cloudinary: leftMedia,
            contentful: module?.leftIcon,
          })

          const RightIcon = determineImage({
            svg: rightSvg,
            cloudinary: rightMedia,
            contentful: module?.rightIcon,
          })

          const gradient = color?.includes('Gradient')

          color = getGradientVariant(color)
          /**
           * Yes, really. The data comes back this way from Contentful.
           * It's possible it's a silly model setup in Contentful.
           */
          const linkUrl = link || file?.file?.file?.url

          if (!linkUrl) {
            return (
              <Placeholder type="warning">
                <Typography variant="small">Missing link</Typography>
                <Typography>{`This ${contentType} is missing a Document or URL link for identifier: ${identifier}`}</Typography>
              </Placeholder>
            )
          }

          const visibilityValue =
            typeof visibility === 'string' ? visibility.toLowerCase() : ''
          const cobraValue =
            typeof cobra === 'string' ? cobra.toLowerCase() : ''

          return (
            <LinkButton
              to={linkUrl}
              variant="hero"
              CustomLink={GatsbyLink}
              color={color ? color.toLowerCase() : 'primary'}
              outlined={outlined}
              gradient={gradient}
              leftIcon={LeftIcon}
              rightIcon={RightIcon}
              mobileText={mobileText}
              visibility={visibilityValue}
              cobra={cobraValue}
            >
              {text}
            </LinkButton>
          )
        }

        case 'ContentfulPhoneNumber': {
          const {
            hidePhoneNumberOnMobile,
            staticPhoneNumber,
            outlined,
            cobra,
            cobraButtonText,
            cobraCartUrl,
            mobileText,
            visibility,
          } = fields
          let { color } = fields
          const gradient = color?.includes('Gradient')

          color = getGradientVariant(color)
          const visibilityValue =
            typeof visibility === 'string' ? visibility.toLowerCase() : ''

          return (
            <LeshenPhoneCTA
              variant="hero"
              hidePhoneNumberOnMobile={hidePhoneNumberOnMobile}
              staticPhoneNumber={staticPhoneNumber}
              color={color ? color.toLowerCase() : 'primary'}
              outlined={outlined}
              gradient={gradient}
              cobra={cobra}
              cobraButtonText={cobraButtonText}
              cobraCartUrl={cobraCartUrl}
              mobileText={mobileText}
              visibility={visibilityValue}
            >
              {fields.text}
            </LeshenPhoneCTA>
          )
        }

        case 'ContentfulPrice': {
          const { brandy, variant, modal, showFeaturedBullets } = fields

          return (
            <Price
              standalone
              before={brandy?.price?.before}
              className="price embedded-entry"
              {...brandy?.price}
              bullets={brandy?.bullets}
              showFeaturedBullets={showFeaturedBullets}
              variant={variant}
              nutrition={brandy?.nutrition}
            >
              {modal && (
                <ModalWithState
                  modalText={renderRichText(modal.modalTrigger, mapRichText())}
                  heading={modal?.heading}
                  noExit={modal?.noExit}
                >
                  {renderRichText(modal?.mainContent, mapRichText())}
                </ModalWithState>
              )}
            </Price>
          )
        }
        default: {
          return mapRichTextEmbed(node)
        }
      }
    },
    [INLINES.HYPERLINK]: (node = {}, children) => (
      <Link to={node?.data?.uri}>{children}</Link>
    ),
  },
  renderMark: {
    [MARKS.BOLD]: (text) => <strong>{text}</strong>,
    [MARKS.ITALIC]: (text) => <em>{text}</em>,
    [MARKS.SUPERSCRIPT]: (text) => <sup>{text}</sup>,
    [MARKS.SUBSCRIPT]: (text) => <sub>{text}</sub>,
  },
  renderText: (text) =>
    typeof text === 'string' &&
    text
      .split('\n')
      .reduce(
        (children, textSegment, index) => [
          ...children,
          index > 0 && <br />,
          textSegment,
        ],
        []
      ),
}

/**
 * Determines the component "Item" within a "Collection" to be rendered.
 * @param {object} item - Contains the `__typename` (GraphQL default field), brandy data (if applicable),
 * and/or content for a block.
 * @returns {JSX} React component: PackageCard | PullQuote | Column | Placeholder
 */
export const mapItems = (item, sectionType, options) => {
  const { __typename: typename } = item
  let cta

  const cloudinaryImageData =
    item?.image?.cloudinary?.[0]?.gatsbyImageData ?? null
  const imageContext = item?.image?.cloudinary?.[0]?.context ?? null

  if (cloudinaryImageData) {
    cloudinaryImageData.context = imageContext
  }

  const svg = item?.image?.cloudinary?.[0]?.svg ?? null
  const { borderRadius, customPosition, aspectRatio } = item || {}

  const ImageComponent = determineImage({
    svg,
    cloudinary: cloudinaryImageData,
    contentful: item?.image?.media ? item?.image : null, // We specifically need to pass null here because "contentful" isn't falsey by default in this case
    borderRadius,
    customPosition,
    aspectRatio,
  })

  switch (typename) {
    case 'ContentfulSpeedTest': {
      return <SpeedTest id={item.speedTestId} style={item?.style} />
    }
    case 'ContentfulCountdownTimer': {
      return (
        <CountdownTimer
          key={item.id}
          startDate={item?.startDate}
          endDate={item.endDate}
          text={
            item?.countdownText &&
            renderRichText(item?.countdownText, mapRichText())
          }
          style={item?.style}
        />
      )
    }
    case 'ContentfulMedia': {
      return ImageComponent
    }
    case 'ContentfulPackage':
      if (!item.brandy) {
        return (
          <Placeholder key={item.id} type="error">
            <Typography variant="small">Error:</Typography>
            <Typography>{`No brandy package data found for ID: ${item.identifier}`}</Typography>
            <br />
            <Typography>
              Double check the brand option that you are passing to
              gatsby-source-brandy.
            </Typography>
          </Placeholder>
        )
      }

      if (item.cta) {
        const {
          __typename: type,
          text,
          outlined,
          staticPhoneNumber,
          hidePhoneNumberOnMobile,
          link,
          cobra,
          cobraButtonText,
          cobraCartUrl,
          mobileText,
          visibility,
        } = item.cta

        const leftMedia =
          item?.leftIcon?.cloudinary?.[0]?.gatsbyImageData ?? null
        const leftSvg = item?.leftIcon?.cloudinary?.[0]?.svg ?? null
        const LeftIcon = leftSvg ? (
          <SVGImage
            svg={leftSvg.code}
            presentationWidth={leftSvg.presentationWidth}
          />
        ) : (
          <Image data={leftMedia} />
        )

        const rightMedia =
          item?.rightIcon?.cloudinary?.[0]?.gatsbyImageData ?? null
        const rightSvg = item?.rightIcon?.cloudinary?.[0]?.svg ?? null

        const RightIcon = rightSvg ? (
          <SVGImage
            svg={rightSvg.code}
            presentationWidth={rightSvg.presentationWidth}
          />
        ) : (
          <Image data={rightMedia} />
        )

        let { color } = item.cta

        const gradient = color?.includes('Gradient')
        color = getGradientVariant(color)

        const visibilityValue =
          typeof visibility === 'string' ? visibility.toLowerCase() : ''

        const cobraValue = typeof cobra === 'string' ? cobra.toLowerCase() : ''

        switch (type) {
          case 'ContentfulPhoneNumber':
            cta = (
              <LeshenPhoneCTA
                variant="feature"
                hidePhoneNumberOnMobile={hidePhoneNumberOnMobile}
                staticPhoneNumber={staticPhoneNumber}
                color={color ? color.toLowerCase() : 'primary'}
                outlined={outlined}
                gradient={gradient}
                cobra={cobra}
                cobraButtonText={cobraButtonText}
                cobraCartUrl={cobraCartUrl}
                mobileText={mobileText}
                visibility={visibilityValue}
              >
                {text}
              </LeshenPhoneCTA>
            )
            break
          default:
            cta = (
              <LinkButton
                variant="feature"
                to={link}
                CustomLink={GatsbyLink}
                color={color ? color.toLowerCase() : 'primary'}
                outlined={outlined}
                gradient={gradient}
                leftIcon={LeftIcon}
                rightIcon={RightIcon}
                visibility={visibilityValue}
                cobra={cobraValue}
              >
                {text}
              </LinkButton>
            )
        }
      }
      return (
        <PackageCard
          mobileCarousel={options?.mobileCarousel}
          label={item.label && item.label}
          packageData={{ ...item.brandy }}
          cta={cta}
          key={item.id}
          content={item.content && renderRichText(item.content, mapRichText())}
          sectionType={sectionType}
          layout={item.layout}
          image={ImageComponent}
          modal={
            item.modal && (
              <ModalWithState
                modalText={renderRichText(
                  item.modal.modalTrigger,
                  mapRichText()
                )}
                heading={item.modal.heading}
                noExit={item?.modal?.noExit}
              >
                {renderRichText(item.modal.mainContent, mapRichText())}
              </ModalWithState>
            )
          }
        />
      )

    case 'ContentfulPullquote':
      return (
        <Pullquote key={item.id} cite={item?.attribution}>
          {item?.quote?.quote}
        </Pullquote>
      )

    case 'ContentfulBlock': {
      return (
        <Column
          key={item.id}
          image={ImageComponent}
          svg={svg}
          backgroundColor={formatBackgroundColor(item?.backgroundColor)}
          borderColor={item.borderColor?.toLowerCase()}
          borderType={item.borderType?.toLowerCase()?.replace(' ', '')}
          aspectRatio={item?.image?.aspectRatio}
          borderRadius={item?.image?.borderRadius}
          customPosition={item?.image?.customPosition}
          boxShadow={options?.boxShadow}
        >
          {item.heading && <Typography variant="h4">{item.heading}</Typography>}
          {item.subheading && (
            <Typography variant="h5">{item.subheading}</Typography>
          )}
          {item.mainContent &&
            renderRichText(
              item.mainContent,
              mapRichText({
                listIcon: determineImage({ contentful: item?.listIcon }),
              })
            )}
        </Column>
      )
    }
    default:
      return (
        <Placeholder key={item.id} type="warning">
          <Typography variant="small">Missing Component</Typography>
          <Typography>
            The {typename} component can&apos;t be rendered here. It is most
            likely missing support from the core.
          </Typography>
        </Placeholder>
      )
  }
}

export const mapModules = (module, options, sectionType) => {
  const { __typename: typename } = module
  let cta
  let { buttonColor } = module
  const gradient = buttonColor?.includes('Gradient')
  buttonColor = getGradientVariant(buttonColor)
  const { spanish = false } = options || {}

  const cloudinaryImageData =
    module?.image?.cloudinary?.[0]?.gatsbyImageData ?? null

  const imageContext = module?.image?.cloudinary?.[0]?.context ?? null

  if (cloudinaryImageData) {
    cloudinaryImageData.context = imageContext
  }

  const svg = module?.cloudinary?.[0]?.svg || null

  const { borderRadius, customPosition, aspectRatio } =
    module.image || module || {}

  const ImageComponent = determineImage({
    svg,
    cloudinary: cloudinaryImageData,
    contentful: module?.image,
    borderRadius,
    customPosition,
    aspectRatio,
  })

  switch (typename) {
    case 'ContentfulSpeedTest': {
      return <SpeedTest id={module.speedTestId} style={module.style} />
    }
    case 'ContentfulPackage':
      if (!module.brandy) {
        return (
          <Placeholder key={module.id} type="error">
            <Typography variant="small">Error:</Typography>
            <Typography>{`No brandy package data found for ID: ${module.identifier}`}</Typography>
            <br />
            <Typography>
              Double check the brand option that you are passing to
              gatsby-source-brandy.
            </Typography>
          </Placeholder>
        )
      }

      if (module.cta) {
        const {
          __typename: type,
          text,
          outlined,
          staticPhoneNumber,
          hidePhoneNumberOnMobile,
          link,
          mobileText,
          visibility,
          cobra,
        } = module.cta
        let { color } = module.cta
        const ctaGradient = color?.includes('Gradient')

        color = getGradientVariant(color)
        const visibilityValue =
          typeof visibility === 'string' ? visibility.toLowerCase() : ''

        const cobraValue = typeof cobra === 'string' ? cobra.toLowerCase() : ''

        switch (type) {
          case 'ContentfulPhoneNumber':
            cta = (
              <LeshenPhoneCTA
                key={module.id}
                variant="feature"
                hidePhoneNumberOnMobile={hidePhoneNumberOnMobile}
                staticPhoneNumber={staticPhoneNumber}
                color={color ? color.toLowerCase() : 'primary'}
                outlined={outlined}
                gradient={ctaGradient}
                mobileText={mobileText}
                visibility={visibilityValue}
              >
                {text}
              </LeshenPhoneCTA>
            )
            break
          default:
            cta = (
              <LinkButton
                key={module.id}
                variant="feature"
                to={link}
                CustomLink={GatsbyLink}
                color={color ? color.toLowerCase() : 'primary'}
                outlined={outlined}
                gradient={ctaGradient}
                visibility={visibilityValue}
                cobra={cobraValue}
              >
                {text}
              </LinkButton>
            )
        }
      }

      return (
        <PackageCard
          image={ImageComponent}
          label={module.label && module.label}
          packageData={{ ...module.brandy }}
          cta={cta}
          key={module.id}
          layout={module.layout}
          content={
            module.content && renderRichText(module.content, mapRichText())
          }
          sectionType={sectionType}
          modal={
            module.modal && (
              <ModalWithState
                modalText={renderRichText(
                  module.modal.modalTrigger,
                  mapRichText()
                )}
                heading={module.modal.heading}
                noExit={module?.modal?.noExit}
              >
                {renderRichText(module?.modal?.mainContent, mapRichText())}
              </ModalWithState>
            )
          }
        />
      )
    case 'ContentfulAvailabilityAddressForm':
      return (
        <AvailabilityAddressForm
          key={module.id}
          label={module.labelText}
          placeholderText={module.placeholderText}
          buttonText={module.buttonText}
          buttonOutlined={module.buttonOutlined}
          buttonColor={buttonColor.toLowerCase()}
          buttonGradient={gradient}
        />
      )
    case 'ContentfulAvailabilityZipForm':
      return (
        <AvailabilityForm
          key={module.id}
          label={module.labelText}
          buttonText={module.buttonText}
          placeholderText={module.placeholderText}
          buttonOutlined={module.buttonOutlined}
          buttonColor={buttonColor.toLowerCase()}
          buttonGradient={gradient}
          formVisibility={module.formVisibility}
        />
      )
    case 'ContentfulBlock': {
      return (
        <Column
          key={module.id}
          image={ImageComponent}
          svg={svg}
          backgroundColor={formatBackgroundColor(module?.backgroundColor)}
          borderColor={module.borderColor?.toLowerCase()}
          listIcon={determineImage({ contentful: module?.listIcon })}
          borderType={module.borderType?.toLowerCase()?.replace(' ', '')}
        >
          {module.heading && (
            <Typography variant="h4">{module.heading}</Typography>
          )}
          {module.subheading && (
            <Typography variant="h5">{module.subheading}</Typography>
          )}
          {module.mainContent &&
            renderRichText(
              module.mainContent,
              mapRichText({
                listIcon: determineImage({ contentful: module?.listIcon }),
              })
            )}
        </Column>
      )
    }
    case 'ContentfulButton': {
      const { identifier, outlined, text, file, link, visibility, cobra } =
        module
      let { color } = module

      const leftMedia =
        module?.leftIcon?.cloudinary?.[0]?.gatsbyImageData ?? null
      const leftSvg = module?.leftIcon?.cloudinary?.[0]?.svg ?? null
      const rightMedia =
        module?.rightIcon?.cloudinary?.[0]?.gatsbyImageData ?? null
      const rightSvg = module?.rightIcon?.cloudinary?.[0]?.svg ?? null

      const LeftIcon = determineImage({
        svg: leftSvg,
        cloudinary: leftMedia,
        contentful: module?.leftIcon,
      })

      const RightIcon = determineImage({
        svg: rightSvg,
        cloudinary: rightMedia,
        contentful: module?.rightIcon,
      })

      const buttonGradient = color?.includes('Gradient')

      color = getGradientVariant(color)
      /**
       * Yes, really. The data comes back this way from Contentful.
       * It's possible it's a silly model setup in Contentful.
       */
      const linkUrl = link || file?.file?.file?.url

      const visibilityValue =
        typeof visibility === 'string' ? visibility.toLowerCase() : ''
      const cobraValue = typeof cobra === 'string' ? cobra.toLowerCase() : ''
      if (!linkUrl) {
        return (
          <Placeholder type="warning" key={module?.id}>
            <Typography variant="small">Missing link</Typography>
            <Typography>
              This ContentfulButton is missing a Document or URL link. Please
              provide one in Contentful.
            </Typography>
            <Typography>The identifier is: &quot;{identifier}&quot;</Typography>
          </Placeholder>
        )
      }

      return (
        <LinkButton
          key={module.id}
          to={linkUrl}
          CustomLink={GatsbyLink}
          color={color ? color.toLowerCase() : 'primary'}
          outlined={outlined}
          gradient={buttonGradient}
          leftIcon={LeftIcon}
          rightIcon={RightIcon}
          visibility={visibilityValue}
          cobra={cobraValue}
        >
          {text}
        </LinkButton>
      )
    }
    case 'ContentfulMedia': {
      return ImageComponent
    }
    case 'ContentfulLink': {
      const { text, destination } = module
      return (
        <Link key={module.id} to={destination}>
          {text}
        </Link>
      )
    }
    case 'ContentfulPhoneNumber': {
      const {
        hidePhoneNumberOnMobile,
        staticPhoneNumber,
        outlined,
        cobra,
        cobraButtonText,
        cobraCartUrl,
        mobileText,
        visibility,
      } = module
      let { color } = module
      const phoneNumberGradient = color?.includes('Gradient')
      color = getGradientVariant(color)
      const visibilityValue =
        typeof visibility === 'string' ? visibility.toLowerCase() : ''

      return (
        <LeshenPhoneCTA
          key={module.id}
          hidePhoneNumberOnMobile={hidePhoneNumberOnMobile}
          staticPhoneNumber={staticPhoneNumber}
          color={color ? color.toLowerCase() : 'primary'}
          outlined={outlined}
          gradient={phoneNumberGradient}
          cobra={cobra}
          cobraButtonText={cobraButtonText}
          cobraCartUrl={cobraCartUrl}
          mobileText={mobileText}
          visibility={visibilityValue}
        >
          {module.text}
        </LeshenPhoneCTA>
      )
    }
    case 'ContentfulCountdownTimer': {
      return (
        <CountdownTimer
          key={module.id}
          startDate={module?.startDate}
          endDate={module.endDate}
          text={
            module?.countdownText &&
            renderRichText(module?.countdownText, mapRichText())
          }
          style={module?.style}
          spanish={spanish}
        />
      )
    }
    case 'ContentfulCollection':
      if (!module.items) {
        return (
          <Placeholder type="warning" key={module.id}>
            <Typography variant="small">Missing Items</Typography>
            <Typography>
              The Collection could not be rendered due to missing items. This
              may be caused by some of the collection items being
              &apos;draft&apos; status in Contentful.
            </Typography>
          </Placeholder>
        )
      }

      if (module.type === 'Columns') {
        const hasPackageCard = module.items.some(
          (item) => item.__typename === 'ContentfulPackage'
        )

        const hasBrandyLabel = module.items.some(
          (item) => item?.brandy?.labelOffer
        )

        const hasContentfulLabel = module.items.some((item) => item?.label)

        return (
          <Columns
            boxShadow={module.boxShadow}
            key={module.id}
            cardStyled={module.cardStyled}
            mobileCarousel={module.mobileCarousel}
            carousel={module?.carousel}
            leftAligned={module.leftAligned}
            layout={module.layout}
            packageCardLayout={module.packageCardLayout}
            carouselConfig={carouselConfig}
            extraTopSpacingForLabel={
              (hasPackageCard && hasBrandyLabel) || hasContentfulLabel
            }
          >
            {module.items.map((item) =>
              mapItems(
                item,
                null,
                {
                  mobileCarousel: module.mobileCarousel || !module.carousel,
                  boxShadow: module.boxShadow,
                },
                packages
              )
            )}
          </Columns>
        )
      }

      if (module.type === 'Stacked') {
        return (
          <div key={module.id}>
            {module.items.map((item) => mapItems(item, module.type))}
          </div>
        )
      }

      if (
        module.type === 'Carousel' ||
        (module?.carousel && module?.carousel !== 'No')
      ) {
        return (
          <Carousel key={module.id} options={carouselConfig}>
            {module.items.map((item) => mapItems(item))}
          </Carousel>
        )
      }

      if (module.type === 'Accordion' || module.type.includes('Tabs')) {
        const items = module.items.map((item) => ({
          title: item.heading,
          image: determineImage({
            svg: item.image?.cloudinary[0]?.svg,
            cloudinary: item.image?.cloudinary[0]?.gatsbyImageData,
            contentful: item.image?.media,
            borderRadius: item.image?.borderRadius,
            customPosition: item.image?.customPosition,
            aspectRatio: item.image?.aspectRatio,
          }),
          content: (
            <div key={module.id}>
              {item.subheading && (
                <Typography variant="feature">{item.subheading}</Typography>
              )}
              {renderRichText(item.mainContent, mapRichText())}
            </div>
          ),
        }))

        if (module.type.includes('Tabs')) {
          return (
            <Tabs
              key={module.id}
              data={items}
              isVertical={module.type === 'Vertical Tabs'}
            />
          )
        }

        return <Accordion key={module.id} items={items} spanish={spanish} />
      }

      return (
        <Placeholder type="warning" key={module.id}>
          <Typography variant="small">Missing Component</Typography>
          <Typography>
            The {typename}:{module.type} component can&apos;t be rendered here.
            It is most likely missing support from the core.
          </Typography>
        </Placeholder>
      )
    case 'ContentfulDisclaimer':
      if (module?.disclaimerReference?.sys?.urn) {
        const content = module?.brandDisclaimer?.content
        return (
          <RichTextDisclaimer
            content={renderRichText(content, mapRichText())} // eslint-disable-line
          />
        )
      }

      // GraphQL should not allow us to get an empty object.
      // But if we did, it would just render undefined for symbol and text.
      return module.brandy ? (
        <Brandy
          key={module.id}
          symbol={module.brandy.symbol}
          text={module.brandy.text}
          variant="legal"
        />
      ) : (
        <Placeholder key={module.id} type="error">
          <Typography variant="small">Brandy Disclaimer Error</Typography>
          <Typography>This module is missing the brandy field.</Typography>
        </Placeholder>
      )
    case 'ContentfulBrandDisclaimer': {
      const { content } = module
      return (
        <Typography variant="legal">
          {renderRichText(content, mapRichText())}
        </Typography> // eslint-disable-line
      )
    }
    case 'ContentfulForm': {
      const { formDisclaimer } = module

      if (module.emily) {
        const {
          emily_guid: emilyGuid,
          submit,
          formFields,
          visibleFieldCount,
          name,
        } = module.emily

        return (
          <FormWithMapi
            key={name}
            formId={emilyGuid}
            formName={name}
            heading={module.heading}
            subheading={module.subheading}
            tcpaCheckbox={module.tcpaCheckbox}
            formDisclaimerPosition={module.formDisclaimerPosition}
            action={submit.action}
            fields={formFields}
            submitButtonText={submit.buttonText}
            inlineButton={visibleFieldCount === 1}
            disclaimer={formDisclaimer}
            spanish={spanish}
          />
        )
      }
      if (module.emilyMultiStep) {
        const { submit, steps, name } = module.emilyMultiStep

        return (
          <FormWithMapi
            key={name}
            isMultiStep
            formName={name}
            heading={module.heading}
            subheading={module.subheading}
            tcpaCheckbox={module.tcpaCheckbox}
            formDisclaimerPosition={module.formDisclaimerPosition}
            action={submit.action}
            steps={steps}
            submitButtonText={submit.buttonText}
            disclaimer={formDisclaimer}
            spanish={spanish}
          />
        )
      }
      return (
        <Placeholder key={module.id} type="error">
          <Typography variant="small">Form Data Error</Typography>
          <Typography>The GUID for the form is likely incorrect.</Typography>
        </Placeholder>
      )
    }
    case 'ContentfulBasicTable': {
      return (
        <Table
          key={module.tableName}
          leftColumnAlignment={module.leftColumnAlignment}
          mobileComparison={module.mobileComparison}
        >
          {/* we can disable the next line because the order of the functions being defined doesn't matter since they are all called properly in the end. everything is called in the last function: mapSections, which at that point has everything defined  */}
          {/* eslint-disable-next-line */}
          {renderRichText(module.mainContent, mapRichText())}
        </Table>
      )
    }
    case 'ContentfulChannelLineup': {
      if (!module.brandyToken) {
        return (
          <Placeholder key={module.id} type="error">
            <Typography variant="small">Brandy Token Error</Typography>
            <Typography>This Component is missing a Brandy Token.</Typography>
          </Placeholder>
        )
      }
      return (
        <ChannelLineupWithData
          key={module.id}
          brandyToken={module.brandyToken}
        />
      )
    }
    case 'ContentfulModal':
      return (
        <ModalWithState
          key={module.id}
          modalText={renderRichText(module.modalTrigger, mapRichText())}
          heading={module.heading}
          noExit={module.noExit}
        >
          {renderRichText(module.mainContent, mapRichText())}
        </ModalWithState>
      )
    case 'ContentfulVideo': {
      const {
        childrenContentfulVideoCloudinaryJsonNode,
        childContentfulVideoIframeTextNode,
        width,
        height,
      } = module
      const iframe = childContentfulVideoIframeTextNode?.iframe
      const videoUrl =
        childrenContentfulVideoCloudinaryJsonNode?.[0]?.secure_url
      return (
        <Video
          key={module.id}
          height={height}
          width={width}
          iframe={iframe}
          url={videoUrl}
          poster={module?.thumbnail?.cloudinary[0]?.secure_url}
        />
      )
    }
    case 'ContentfulCart': {
      return <Cart key={module.id} />
    }
    case 'ContentfulStateDropdown':
      return (
        <StateDropdown key={module.id} aoa={module.aoa} spanish={spanish} />
      )
    case 'ContentfulMegaForm':
      return (
        <MegaForm
          isInsideModal
          formId={module.form.emily.emily_guid}
          formName={module.form.emily.name}
          heading={module.form.heading}
          tcpaCheckbox={module.form.cpaCheckbox}
          formDisclaimerPosition={module.form.formDisclaimerPosition}
          action={module.form.emily.submit.action}
          submitButtonText={module.form.emily.submit.buttonText}
          fields={module.form.emily.formFields}
          inlineButton={module.form.visibleFieldCount === 1}
          disclaimer={module.form.formDisclaimer}
          stepsData={StepsData}
          key={module.form.identifier}
          spanish={module.form.spanish}
          megaFormDisclaimer={module.form.megaFormDisclaimer}
        >
          {module?.items?.length > 0 && (
            <Columns>{module?.items?.map((item) => mapItems(item))}</Columns>
          )}
        </MegaForm>
      )
    default:
      return (
        <Placeholder key={module.id} type="warning">
          <Typography variant="small">Missing Component</Typography>
          <Typography>
            The {typename} component can&apos;t be rendered here. It is most
            likely missing support from the core.
          </Typography>
        </Placeholder>
      )
  }
}

/**
 * Determines the component to render for each section of a page
 * @param {object} section - Contains the `type` to render (e.g. 'Split Content')
 * @returns {JSX} React component: SplitContent | SplitModule | Billboard | Hero | VariableContent
 */
export const mapSections = (section, options = {}, brandPackages = []) => {
  if (!section) return null
  packages = brandPackages

  if (section.__typename === 'ContentfulMegaForm') {
    const { items, form, megaFormDisclaimer } = section

    const heading = form?.heading ?? null
    const tcpaCheckbox = form?.tcpaCheckbox ?? null
    const formDisclaimerPosition = form?.formDisclaimerPosition ?? null
    const emily = form?.emily ?? null
    const formDisclaimer = form?.formDisclaimer ?? null

    const { spanish } = options

    const {
      emily_guid: emilyGuid,
      submit,
      formFields,
      visibleFieldCount,
      name,
    } = emily

    if (!emily) {
      return (
        <Placeholder key={section.id} type="error">
          <Typography variant="small">Error</Typography>
          <Typography>
            No data could be found for this component. Check to make sure the
            GUID is correct. If you think the data should exist, A clean rebuild
            may be required.
          </Typography>
        </Placeholder>
      )
    }

    return (
      <MegaForm
        formId={emilyGuid}
        formName={name}
        heading={heading}
        tcpaCheckbox={tcpaCheckbox}
        formDisclaimerPosition={formDisclaimerPosition}
        action={submit.action}
        fields={formFields}
        submitButtonText={submit.buttonText}
        inlineButton={visibleFieldCount === 1}
        disclaimer={formDisclaimer}
        stepsData={StepsData}
        key={section.id}
        spanish={spanish}
        megaFormDisclaimer={megaFormDisclaimer}
      >
        {items?.length > 0 && (
          <Columns>{items?.map((item) => mapItems(item))}</Columns>
        )}
      </MegaForm>
    )
  }

  const {
    id,
    type,
    mainContent,
    featuredImage,
    fullWidthFeaturedImage,
    mobileImage,
    backgroundColor,
    backgroundImage,
    mobileBackgroundImage,
    textColor,
    mobileTextColor,
    alignMainContent,
    alignImageToBottom,
    mobileAlignImageToBottom,
    modules,
    jumpLinkId,
    topPadding,
    bottomPadding,
    width,
    authorLine,
  } = section
  const { mapper, isImageCritical } = options
  const typeSpecificProps = {}
  const imageSpecificProps = {}

  let SectionComponent
  let shouldImageBeFullWidth = false

  /*
   * Both `featuredImageData` and `fullWidthFeaturedImageData` are gatsby image
   * queries that happen for every image. A little inefficient but it allows us to
   * pass the appropriately sized image based on the type of component it should be
   * used in.
   */
  const featuredImageData =
    featuredImage?.cloudinary?.[0]?.gatsbyImageData ||
    featuredImage?.cloudinary?.[0]?.svg ||
    null
  const mobileImageData =
    mobileImage?.cloudinary?.[0]?.gatsbyImageData ||
    mobileImage?.cloudinary?.[0]?.svg ||
    null

  const mobileContentfulMedia = mobileImage?.media
  const contentfulMedia = featuredImage?.media

  const { borderRadius, customPosition, aspectRatio } = featuredImage || {}
  const {
    borderRadius: mobileBorderRadius,
    customPosition: mobileCustomPosition,
    aspectRatio: mobileAspectRatio,
  } = mobileImage || {}

  const fullWidthFeaturedImageData =
    fullWidthFeaturedImage?.cloudinary?.[0]?.gatsbyImageData ?? null
  const backgroundImageData =
    backgroundImage?.cloudinary?.[0]?.gatsbyImageData ?? null

  const contentfulBackgroundImageData = getImage(backgroundImage?.media)
  const contentfulMobileBackgroundImageData = getImage(backgroundImage?.media)

  const mobileBackgroundImageData =
    mobileBackgroundImage?.cloudinary?.[0]?.gatsbyImageData ?? null

  // SVG data will be added to the base `cloudinary` object if it exists.
  // This is used to check if it's an SVG further down, and pass it on.
  const svg = featuredImage?.cloudinary?.[0]?.svg ?? null
  const mobileSvg = mobileImage?.cloudinary?.[0]?.svg ?? null

  // Contains the metadata from cloudinary (alt tag)
  const imageContext = featuredImage?.cloudinary?.[0]?.context ?? null
  let BackgroundImageComponent = null
  let MobileBackgroundImageComponent = null

  if (contentfulBackgroundImageData || backgroundImageData) {
    BackgroundImageComponent = determineImage({
      cloudinary: backgroundImageData,
      contentful: contentfulBackgroundImageData,
      svg,
      useGatsbyImage:
        Boolean(contentfulBackgroundImageData) || Boolean(backgroundImage),
      alt:
        backgroundImage?.media?.description ||
        backgroundImage?.cloudinary?.[0]?.context?.custom?.alt,
    })
  }

  if (contentfulMobileBackgroundImageData || mobileBackgroundImageData || svg) {
    MobileBackgroundImageComponent = determineImage({
      cloudinary: mobileBackgroundImageData,
      contentful: contentfulMobileBackgroundImageData,
      svg: mobileSvg,
      useGatsbyImage:
        Boolean(contentfulMobileBackgroundImageData) ||
        Boolean(mobileBackgroundImage),
      alt:
        mobileBackgroundImage?.media?.description ||
        mobileBackgroundImage?.cloudinary?.[0]?.context?.custom?.alt,
    })
  }

  if (section.__typename === 'ContentfulBanner') {
    const { centerAligned, layout, imageAlignment, contentAlignment } = section

    const ImageComponent = determineImage({
      svg,
      cloudinary: featuredImageData,
      aspectRatio,
      borderRadius,
      customPosition,
    })

    const MobileImageComponent = determineImage({
      svg: mobileSvg,
      cloudinary: mobileImageData,
      aspectRatio,
      borderRadius,
      customPosition,
    })

    return (
      <React.Fragment key={id}>
        {/* If a section has a jump link we will render a div with the corresponding ID.
          This seemed better than prop drilling jumpLinkIdId onto every section component
          but we may want to revisit this. */}
        {jumpLinkId && <div id={jumpLinkId} />}
        <Banner
          className={`background-color-${
            formatBackgroundColor(backgroundColor) || 'default'
          }`}
          backgroundColor={formatBackgroundColor(backgroundColor)}
          backgroundImage={BackgroundImageComponent}
          mobileBackgroundImage={MobileBackgroundImageComponent}
          textColor={textColor}
          mobileTextColor={mobileTextColor}
          image={ImageComponent}
          mobileImage={MobileImageComponent}
          mainContent={
            mainContent && renderRichText(mainContent, mapper || mapRichText())
          }
          centerAligned={centerAligned}
          layout={layout}
          imageAlignment={imageAlignment}
          contentAlignment={contentAlignment}
        />
      </React.Fragment>
    )
  }

  if (section.__typename === 'ContentfulItemBank') {
    const { stackItemsOnMobile } = section

    return (
      <React.Fragment key={id}>
        {jumpLinkId && <div id={jumpLinkId} />}
        <ItemBank
          backgroundColor={formatBackgroundColor(backgroundColor)}
          backgroundImage={BackgroundImageComponent}
          mobileBackgroundImage={MobileBackgroundImageComponent}
          textColor={textColor}
          mobileTextColor={mobileTextColor}
          stackItemsOnMobile={stackItemsOnMobile}
          mainContent={
            mainContent && renderRichText(mainContent, mapper || mapRichText())
          }
        >
          {modules && (
            <>{modules.map((module) => mapModules(module, options, type))}</>
          )}
        </ItemBank>
      </React.Fragment>
    )
  }

  // if (section.__typename === 'ContentfulFeaturedPosts') {
  //   const { tags } = section
  //   console.log(tags)
  //   return <FeaturedPosts tags={tags} />
  // }

  if (section.__typename === 'ContentfulCtaBar') {
    const {
      leftBlock,
      leftDivider,
      middleBlock,
      middleDivider,
      rightBlock,
      sticky,
    } = section
    const color = section?.textColor?.toLowerCase()

    const filteredLeftBlock = removeEmptyParagraphs(leftBlock)
    const filteredMiddleBlock = removeEmptyParagraphs(middleBlock)
    const filteredRightBlock = removeEmptyParagraphs(rightBlock)

    return (
      <CtaBar
        key={id}
        backgroundColor={formatBackgroundColor(section?.backgroundColor)}
        textColor={color}
        sticky={sticky}
        leftBlock={
          filteredLeftBlock &&
          renderRichText(filteredLeftBlock, mapper || mapRichText())
        }
        leftDivider={leftDivider}
        middleBlock={
          filteredMiddleBlock &&
          renderRichText(filteredMiddleBlock, mapper || mapRichText())
        }
        middleDivider={middleDivider}
        rightBlock={
          filteredRightBlock &&
          renderRichText(filteredRightBlock, mapper || mapRichText())
        }
      />
    )
  }

  switch (type) {
    case 'Split Content':
      {
        const imageType = featuredImageData?.svg?.presentationWidth
          ? 'svg'
          : 'fluid'

        const mobileImageType = mobileImageData?.svg?.presentationWidth
          ? 'svg'
          : 'fluid'

        typeSpecificProps.imageWidth =
          featuredImageData?.[imageType]?.presentationWidth

        typeSpecificProps.borderRadius = section.borderRadius

        typeSpecificProps.imageWidth =
          mobileImageData?.[mobileImageType]?.presentationWidth

        SectionComponent = SplitContent
        typeSpecificProps.alignMainContent =
          alignMainContent?.toLowerCase() || 'left'
        typeSpecificProps.alignImageToBottom = alignImageToBottom
        typeSpecificProps.mobileAlignImageToBottom = mobileAlignImageToBottom
        typeSpecificProps.aspectRatio = featuredImage?.aspectRatio || ''
        typeSpecificProps.borderRadius = featuredImage?.borderRadius || ''
        typeSpecificProps.customPosition = featuredImage?.customPosition || ''
      }
      break
    case 'Split Module':
      SectionComponent = SplitModule
      typeSpecificProps.alignMainContent =
        alignMainContent?.toLowerCase() || 'left'
      typeSpecificProps.alignImageToBottom = alignImageToBottom
      typeSpecificProps.mobileAlignImageToBottom = mobileAlignImageToBottom
      break
    case 'Billboard':
      typeSpecificProps.alignImageToBottom = alignImageToBottom
      typeSpecificProps.mobileAlignImageToBottom = mobileAlignImageToBottom
      SectionComponent = Billboard
      typeSpecificProps.variant = 'full'
      typeSpecificProps.CustomLink = GatsbyLink
      imageSpecificProps.maxWidth = 'none'
      shouldImageBeFullWidth = true
      typeSpecificProps.alignMainContent =
        alignMainContent?.toLowerCase() || 'left'
      break
    case 'Split Billboard':
      typeSpecificProps.alignImageToBottom = alignImageToBottom
      typeSpecificProps.mobileAlignImageToBottom = mobileAlignImageToBottom
      SectionComponent = Billboard
      typeSpecificProps.CustomLink = GatsbyLink
      imageSpecificProps.maxWidth = 'none'
      shouldImageBeFullWidth = true
      typeSpecificProps.alignMainContent =
        alignMainContent?.toLowerCase() || 'left'
      break
    case 'Hero':
      typeSpecificProps.alignImageToBottom = alignImageToBottom
      typeSpecificProps.mobileAlignImageToBottom = mobileAlignImageToBottom
      SectionComponent = Hero
      typeSpecificProps.CustomLink = GatsbyLink
      shouldImageBeFullWidth = true
      typeSpecificProps.alignMainContent =
        alignMainContent?.toLowerCase() || 'left'
      break
    default:
      SectionComponent = VariableContent
      typeSpecificProps.alignMainContent =
        alignMainContent?.toLowerCase() || 'center'
      typeSpecificProps.alignImageToBottom = alignImageToBottom
      typeSpecificProps.mobileAlignImageToBottom = mobileAlignImageToBottom
      typeSpecificProps.CustomLink = GatsbyLink
      typeSpecificProps.centeredContent = true
      typeSpecificProps.authorLine = authorLine
      typeSpecificProps.aspectRatio = aspectRatio
      typeSpecificProps.borderRadius = borderRadius
      typeSpecificProps.customPosition = customPosition
  }

  /**
   * I'm not really a fan of this approach in general, but I'm not
   * smart enough at the moment to figure a way around these 3 states
   * with simpler/cleaner logic.
   */
  let ImageComponent = null
  let MobileImageComponent = null

  if (
    (featuredImageData && fullWidthFeaturedImageData) ||
    contentfulMedia ||
    svg
  ) {
    const imageData =
      (shouldImageBeFullWidth
        ? fullWidthFeaturedImageData
        : featuredImageData) ||
      svg ||
      {}
    imageData.context = imageContext
    ImageComponent = determineImage({
      cloudinary: imageData,
      contentful: featuredImage?.media ? featuredImage : null,
      aspectRatio: typeSpecificProps?.aspectRatio,
      borderRadius: typeSpecificProps?.borderRadius,
      customPosition: typeSpecificProps?.customPosition,
      isImageCritical,
      svg,
      alt: contentfulMedia?.description || imageData?.context?.alt,
    })

    MobileImageComponent = determineImage({
      cloudinary: mobileImageData,
      contentful: featuredImage?.media ? featuredImage : null,
      aspectRatio: typeSpecificProps?.aspectRatio,
      borderRadius: typeSpecificProps?.borderRadius,
      customPosition: typeSpecificProps?.customPosition,
      isImageCritical,
      alt: contentfulMedia?.description || imageData?.context?.alt,
      svg: mobileSvg,
    })
  }
  if (mobileContentfulMedia) {
    MobileImageComponent = determineImage({
      contentful: mobileImage,
      borderRadius: mobileBorderRadius,
      customPosition: mobileCustomPosition,
      aspectRatio: mobileAspectRatio,
    })
  }

  return (
    <React.Fragment key={section.id}>
      {/* If a section has a jump link we will render a div with the corresponding ID.
          This seemed better than prop drilling jumpLinkIdId onto every section component
          but we may want to revisit this. */}
      {jumpLinkId && <div id={jumpLinkId} />}
      <SectionComponent
        key={id}
        mainContent={
          mainContent && renderRichText(mainContent, mapper || mapRichText())
        }
        splitBackgroundColor={formatBackgroundColor(
          section?.splitBackgroundColor
        )}
        backgroundColor={formatBackgroundColor(section?.backgroundColor)}
        image={ImageComponent}
        mobileImage={MobileImageComponent}
        backgroundImage={BackgroundImageComponent}
        mobileBackgroundImage={MobileBackgroundImageComponent}
        textColor={textColor}
        mobileTextColor={mobileTextColor}
        topPadding={topPadding}
        bottomPadding={bottomPadding}
        width={width}
        {...typeSpecificProps}
      >
        {modules && (
          <Stack
            spacing="xxl"
            alignMainContent={typeSpecificProps.alignMainContent}
          >
            {modules.map((module) =>
              mapModules(module, options, type, packages)
            )}
          </Stack>
        )}
      </SectionComponent>
    </React.Fragment>
  )
}
